import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import PostCard from "../components/PostCard";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Globe, Lock, Trash, Stack, X } from "@phosphor-icons/react";

export default function CollectionDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [col, setCol] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingPublic, setSavingPublic] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [c, p] = await Promise.all([
                api.get(`/collections/${id}`),
                api.get(`/collections/${id}/posts`),
            ]);
            setCol(c.data);
            setPosts(p.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
            navigate("/collections");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

    const isOwner = user && col && (user.id === col.user_id || user.role === "admin");

    const togglePublic = async () => {
        if (savingPublic) return;
        setSavingPublic(true);
        try {
            const { data } = await api.put(`/collections/${id}`, { is_public: !col.is_public });
            setCol(data);
            toast.success(data.is_public ? "Now public" : "Set to private");
        } catch (_) {
            toast.error("Could not update");
        } finally {
            setSavingPublic(false);
        }
    };

    const removePost = async (pid) => {
        try {
            await api.delete(`/collections/${id}/posts/${pid}`);
            setPosts((cur) => cur.filter((p) => p.id !== pid));
            toast.success("Removed from trip plan");
        } catch (_) {
            toast.error("Could not remove");
        }
    };

    const deleteCollection = async () => {
        if (!window.confirm("Delete this trip plan? This cannot be undone.")) return;
        try {
            await api.delete(`/collections/${id}`);
            toast.success("Trip plan deleted");
            navigate("/collections");
        } catch (_) {
            toast.error("Could not delete");
        }
    };

    if (loading || !col) {
        return (
            <div className="min-h-screen bg-[#FAFAFA]">
                <Navbar />
                <div className="max-w-3xl mx-auto px-4 py-12 text-center text-stone-500 text-sm">Loading…</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="collection-detail-page">
            <Navbar />

            <header className="bg-gradient-to-br from-[#990000] to-[#7A0000] relative overflow-hidden">
                <div className="absolute -right-12 -top-12 w-72 h-72 rounded-full bg-[#FFCC00]/10 blur-3xl" />
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 relative">
                    <Link to="/collections" data-testid="collection-back-link"
                          className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-6">
                        <ArrowLeft size={16} /> All trip plans
                    </Link>
                    <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFCC00] text-[#5C0000] text-[10px] uppercase tracking-wider font-bold">
                            <Stack size={11} weight="bold" /> Trip Plan
                        </span>
                        {col.is_public ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/70 font-bold">
                                <Globe size={11} weight="bold" /> Public
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/70 font-bold">
                                <Lock size={11} weight="bold" /> Private
                            </span>
                        )}
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter mb-3">
                        {col.title}
                    </h1>
                    {col.description && (
                        <p className="text-white/80 max-w-2xl mb-4">{col.description}</p>
                    )}
                    <div className="text-xs text-white/60">
                        by <Link to={`/users/${col.user_id}`} className="text-[#FFCC00] hover:underline font-semibold">{col.user_name}</Link>
                        <span className="mx-2">·</span>
                        {col.post_count} posts
                    </div>

                    {isOwner && (
                        <div className="mt-6 flex flex-wrap gap-3 items-center">
                            <div className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 flex items-center gap-3">
                                <span className="text-xs uppercase tracking-wider font-bold text-white/80">Public</span>
                                <Switch
                                    data-testid="collection-public-toggle"
                                    checked={col.is_public}
                                    onCheckedChange={togglePublic}
                                    disabled={savingPublic}
                                />
                            </div>
                            <Button
                                data-testid="collection-delete-btn"
                                variant="outline"
                                onClick={deleteCollection}
                                className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white h-9 px-4 text-xs"
                            >
                                <Trash size={12} className="mr-1.5" /> Delete plan
                            </Button>
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {posts.length === 0 ? (
                    <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
                        <Stack size={36} className="mx-auto text-[#990000]/30 mb-3" weight="regular" />
                        <div className="font-heading text-lg font-semibold text-[#0A0A0A] mb-2">No posts in this plan yet</div>
                        <p className="text-stone-500 text-sm mb-5 max-w-sm mx-auto">
                            Browse the feed and tap the folder icon on any post to add it to this trip plan.
                        </p>
                        <Link to="/feed" className="inline-flex items-center px-5 h-10 rounded-lg bg-[#990000] text-white hover:bg-[#7A0000] text-sm font-medium">
                            Browse posts
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {posts.map((p, i) => (
                            <div key={p.id} className="relative">
                                <PostCard post={p} index={i} onUpdate={(updated) => setPosts((cur) => cur.map((x) => x.id === updated.id ? updated : x))} />
                                {isOwner && (
                                    <button
                                        data-testid={`collection-remove-post-${p.id}`}
                                        onClick={() => removePost(p.id)}
                                        title="Remove from trip plan"
                                        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white border border-stone-200 hover:border-[#990000] hover:text-[#990000] text-stone-500 flex items-center justify-center shadow-sm"
                                    >
                                        <X size={14} weight="bold" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
